/**
 * 🗄️ Session Manager Component
 * Provides UI for managing saved sessions - save, load, delete, resume
 * Integrates with the session persistence system
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useSessionPersistence from '../hooks/useSessionPersistence';

// Import centralized theme components
import { 
  Button, 
  Card, 
  Input, 
  Textarea, 
  Text, 
  Heading, 
  Modal, 
  Badge, 
  Alert,
  Spinner,
  FormGroup 
} from './ui/index.jsx';

const SessionManager = ({ 
    currentSessionData = null, 
    onLoadSession = null, 
    onSaveComplete = null,
    showSaveButton = true,
    showLoadButton = true,
    className = "" 
}) => {
    const { user } = useAuth();
    const {
        sessions,
        loading,
        error,
        saveSession,
        loadSession,
        loadUserSessions,
        deleteSession,
        canSaveSessions,
        generateSessionTitle,
        calculateCompletionPercentage
    } = useSessionPersistence();

    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [showLoadDialog, setShowLoadDialog] = useState(false);
    const [saveTitle, setSaveTitle] = useState('');
    const [saveDescription, setSaveDescription] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState(null);

    // Auto-generate title when save dialog opens
    useEffect(() => {
        if (showSaveDialog && currentSessionData) {
            setSaveTitle(generateSessionTitle(currentSessionData));
        }
    }, [showSaveDialog, currentSessionData, generateSessionTitle]);

    // Handle save session
    const handleSaveSession = async () => {
        if (!currentSessionData || !saveTitle.trim()) return;

        try {
            setIsSaving(true);
            
            const savedSession = await saveSession(currentSessionData, {
                title: saveTitle.trim(),
                description: saveDescription.trim(),
                isAutoSave: false
            });

            if (savedSession) {
                setShowSaveDialog(false);
                setSaveTitle('');
                setSaveDescription('');
                
                if (onSaveComplete) {
                    onSaveComplete(savedSession);
                }

                // Show success message
                if (typeof window.showToast === 'function') {
                    window.showToast(`Session "${saveTitle}" saved successfully!`, 'success');
                }
            }
        } catch (error) {
            if (import.meta.env.DEV) {
            }
            if (typeof window.showToast === 'function') {
                window.showToast('Failed to save session', 'error');
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Handle load session
    const handleLoadSession = async (sessionId) => {
        try {
            const sessionData = await loadSession(sessionId);
            
            if (sessionData && onLoadSession) {
                setShowLoadDialog(false);
                onLoadSession(sessionData);
                
                // Show success message
                if (typeof window.showToast === 'function') {
                    window.showToast(`Session "${sessionData.title}" loaded successfully!`, 'success');
                }
            }
        } catch (error) {
            if (import.meta.env.DEV) {
            }
            if (typeof window.showToast === 'function') {
                window.showToast('Failed to load session', 'error');
            }
        }
    };

    // Handle delete session
    const handleDeleteSession = async (sessionId, sessionTitle) => {
        setSessionToDelete({ id: sessionId, title: sessionTitle });
        setShowDeleteConfirm(true);
    };

    // Confirm delete session
    const confirmDeleteSession = async () => {
        if (!sessionToDelete) return;

        try {
            const success = await deleteSession(sessionToDelete.id);
            
            if (success) {
                if (typeof window.showToast === 'function') {
                    window.showToast(`Session "${sessionToDelete.title}" deleted`, 'success');
                }
            }
        } catch (error) {
            if (import.meta.env.DEV) {
            }
            if (typeof window.showToast === 'function') {
                window.showToast('Failed to delete session', 'error');
            }
        } finally {
            setShowDeleteConfirm(false);
            setSessionToDelete(null);
        }
    };

    // Cancel delete
    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setSessionToDelete(null);
    };

    // Format date for display
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch {
            return 'Unknown';
        }
    };

    // Get completion status variant for badges
    const getCompletionVariant = (percentage) => {
        if (percentage >= 100) return 'success';
        if (percentage >= 80) return 'info';
        if (percentage >= 50) return 'warning';
        return 'error';
    };

    if (!canSaveSessions) {
        return (
            <Alert variant="info" className={className}>
                <div className="text-center">
                    <div className="text-[#FFD700] mb-2">🔑</div>
                    <Text variant="gold" weight="medium">Sign in with Google to access session management</Text>
                    <Text variant="secondary" size="sm" className="mt-1">Save your progress and continue your projects anytime</Text>
                </div>
            </Alert>
        );
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Error Display */}
            {error && (
                <Alert variant="error">
                    {error}
                </Alert>
            )}

            {/* Delete Confirmation Modal */}
            <Modal 
                isOpen={showDeleteConfirm} 
                onClose={cancelDelete}
                size="md"
            >
                <div className="text-center">
                    <Heading size="lg" className="mb-4">
                        Confirm Delete
                    </Heading>
                    <Text className="mb-6">
                        Are you sure you want to delete "{sessionToDelete?.title}"?
                    </Text>
                    <Text variant="secondary" size="sm" className="mb-8">
                        This action cannot be undone.
                    </Text>
                    <div className="flex justify-center space-x-4">
                        <Button 
                            variant="secondary" 
                            onClick={cancelDelete}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={confirmDeleteSession}
                        >
                            Delete Session
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SessionManager;